/**
 * Types for the versioned estimate-upload system.
 *
 * Each upload of an MVMS Syndication Upfront Estimates xlsx becomes one
 * `EstimateVersion`. Versions are immutable and identified by their
 * "As of" date (col B in the source file). A plan binds to a single
 * version by id and never changes — like a quote sent to an agency,
 * locked in once issued.
 */

import { EstimateEntry } from '../media-plan-builder/data/estimateDb';

/** Demo identifier as it appears in column E of the source file. */
export type DemoName = string;

/**
 * Estimate upload "kind" — see EstimateVersion.kind. Two values today;
 * extensible if a third sequence is ever needed (e.g. 'preliminary').
 */
export const ESTIMATE_KINDS = ['upfront', 'scatter'] as const;
export type EstimateKind = typeof ESTIMATE_KINDS[number];

/** Human-readable label for an EstimateKind — used in pill + dropdown UI. */
export function formatEstimateKind(kind: EstimateKind): string {
  return kind === 'scatter' ? 'Scatter' : 'Upfront';
}

/**
 * Estimate "rate card" — a SECOND dimension, orthogonal to `kind`.
 *
 * Some agencies negotiate against their own customized estimates. WPP is the
 * first: its plans pull from the WPP file while every other plan keeps pulling
 * from the general master, unchanged. So an estimate is identified by
 * (primaryYear, kind, rateCard), and each combination is an independent
 * upload stream with its own V1, V2, … sequence.
 *
 * Deliberately NOT a third `kind` value — several call sites hardcode
 * kind === 'upfront' as the default and formatEstimateKind above collapses
 * anything non-scatter to 'Upfront'. A third kind would make each of those
 * silently mis-classify WPP.
 *
 * 'general' means exactly what every pre-rate-card estimate meant. The
 * backend COALESCEs a NULL column to 'general', so this is never undefined
 * on a version that came from the API.
 */
export const RATE_CARDS = ['general', 'wpp'] as const;
export type RateCard = typeof RATE_CARDS[number];

/**
 * Human-readable label for a RateCard.
 *
 * 'general' returns null rather than a string: a general estimate is labelled
 * by its kind alone ("Upfront V4"), unchanged from before rate cards existed.
 * Only a custom card contributes a label of its own ("WPP V1"). Callers treat
 * null as "nothing to add".
 */
export function formatRateCard(card: RateCard): string | null {
  // Whitelist the ONE card that has no label of its own, rather than
  // special-casing the one that does. Written the other way round
  // (`card === 'wpp' ? 'WPP' : null`) every future card would collapse to
  // null → "General": the New Plan picker derives both its options and its
  // reverse lookup from this function, so a third card would render a second
  // row labelled "General" and `.find` would resolve it back to 'general' —
  // the user picks the new card and silently gets the master estimate.
  if (card === 'general') return null;
  return card.toUpperCase();
}

/**
 * Display label for an estimate version's stream, e.g. "Upfront" or "WPP".
 * The rate card replaces the kind rather than prefixing it — a WPP card is
 * upfront by definition (there is no WPP scatter), so "WPP Upfront" would be
 * redundant.
 */
export function formatEstimateStream(kind: EstimateKind, card: RateCard): string {
  const label = formatRateCard(card);
  if (!label) return formatEstimateKind(kind);
  // A custom card is upfront by definition — there is no WPP scatter, and
  // the upload UI, uploadNewVersion and the backend all reject the
  // combination — so "WPP Upfront" would be redundant and the card name
  // alone is the right label.
  //
  // But a row CAN arrive outside those paths: the 25/26 scatter data came
  // in via a one-off script, and a backfill or direct INSERT bypasses every
  // guard. Without this branch a (scatter, wpp) row would render as plain
  // "WPP", and since getVersionLabel sequences per (year, kind, card) a WPP
  // upfront and a WPP scatter in the same season would BOTH read "WPP V1"
  // — in the version list, the plan pill, the pill dropdown and the YoY
  // tooltip. Two different estimates, one label, no way to tell them apart.
  return kind === 'scatter' ? `${label} Scatter` : label;
}

/**
 * Normalize a program name for case- / whitespace-insensitive lookup.
 * "Modern Family WKND  " and "modern family wknd" both normalize to
 * "modern family wknd" so they hit the same entry in
 * `EstimateVersion.programNameIndex`.
 */
export function normalizeProgramName(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * A single parsed quarter label as it appeared in row 2 of the source file
 * (e.g., "4Q'26", "1Q'27"). We carry both the raw label and the parsed
 * (quarter, year) tuple so MediaPlanBuilder can match deals (which store
 * quarter + year separately) without re-parsing.
 */
/**
 * Provenance stamp on a SYNTHETIC version built by merging a late quarter into
 * a real one. Absent on every version that came from an upload.
 *
 * A merged version carries a negative synthetic id (see `mergedVersionIdFor`),
 * which exists purely to key the estimate cache and matches no database row.
 * Anything that PERSISTS a version id — the save payload's
 * `estimateVersionId`, most of all — must write `baseId`, never `id`. Keeping
 * the provenance on the object rather than in component state means that
 * distinction travels with the version and cannot go stale.
 */
export interface MergedVersionProvenance {
  /** The real EST_VERSION.ID this version's own quarters came from. */
  baseId: number;
  /** The real EST_VERSION.ID that supplied the merged-in late quarter. */
  overlapId: number;
}

export interface QuarterLabel {
  /** Raw label as shown in UI tabs, e.g. "4Q'26". */
  label: string;
  /** Quarter component, e.g. "Q4". */
  quarter: string;
  /** Year component, e.g. 2026. */
  year: number;
  /** Stable key for React lists / state, e.g. "4Q26". */
  key: string;
}

/**
 * One uploaded snapshot of MVMS Sales Estimate data. Persisted in
 * localStorage under the `estimateVersions` key. Once written, never
 * mutated — new uploads create new versions.
 */
export interface EstimateVersion {
  /** Auto-incremented integer (1, 2, 3, …). */
  id: number;
  /** "As of" date from col B of the source file, ISO-8601 (YYYY-MM-DD). */
  asOfDate: string;
  /** When the user actually uploaded (browser local timestamp). */
  uploadedAt: string;
  /** Uploader email if available (from session). */
  uploadedBy?: string;
  /** Source filename, e.g. "MVMS Syndication 26-27 Season Upfront Estimates.xlsx". */
  fileName: string;
  /** Number of data rows in the source file (rows 3..N). */
  rowCount: number;

  /**
   * The 4 quarter labels from row 2 cols G–J of the source file.
   * These become the QUARTER_TABS for any plan bound to this version.
   */
  quarters: QuarterLabel[];

  /**
   * The primary year of this version — used to derive the year-shift
   * between SF deal data (typically one season behind) and estimate
   * quarters. Equals quarters[0].year.
   */
  primaryYear: number;

  /**
   * Estimate "kind" — distinguishes the original / default upload track
   * ('upfront') from the in-season refresh track ('scatter'). Each kind
   * has its own independent version sequence within a primary year:
   * Upfront V1, V2, … alongside Scatter V1, V2, …
   *
   * Backfilled by the DDL default for all pre-feature rows.
   */
  kind: EstimateKind;

  /**
   * Which agency rate card this estimate belongs to — 'general' (the master
   * estimate every plan used before rate cards existed) or a custom card such
   * as 'wpp'.
   *
   * Orthogonal to `kind`: the version sequence runs per
   * (primaryYear, kind, rateCard), so WPP has its own V1 rather than
   * continuing General's numbering.
   *
   * Optional on the type because versions restored from the legacy
   * localStorage payload predate the field; treat a missing value as
   * 'general'. Versions from the API always carry it — the backend COALESCEs
   * a NULL column.
   */
  rateCard?: RateCard;

  /**
   * Drop-in-compatible estimateDb keyed exactly like the legacy
   * ESTIMATE_DB constant: `${program}|${day}|${period}|${demo}RTG`.
   * MediaPlanBuilder consumes this without changes.
   */
  estimateDb: Record<string, EstimateEntry>;

  /**
   * Lookup map for case- and whitespace-insensitive program name
   * resolution. Salesforce data often has minor casing/whitespace
   * differences from the workbook (e.g. "Modern Family WKND" vs
   * "Modern Family Wknd"). At lookup time the consumer normalizes the
   * input program with `normalizeProgramName()` and checks this map
   * for the canonical name to use in `estimateDb` keys.
   *
   * Key: normalized program name (trimmed, collapsed whitespace, lower).
   * Value: canonical program name exactly as it appears in `estimateDb`
   *        keys.
   */
  programNameIndex: Record<string, string>;

  /**
   * Demo → Universe lookup. New file includes Universe in col D, and
   * we've verified it's consistent across all programs for each demo.
   * Drives the "Universe" boxes at the bottom of the plan UI.
   */
  universeData: Record<DemoName, number>;

  /**
   * Projected (post-uplift) values, kept for reference only. Same key
   * shape as `estimateDb` but populated from cols K–N (IMPS) and S–V
   * (Rating). MediaPlanBuilder does NOT consume this — it's stored so
   * a future "Projected vs Sales Estimate" view can pull from it.
   */
  projectedEstimateDb: Record<string, EstimateEntry>;

  /**
   * Set ONLY on a synthetic version produced by merging a late quarter into a
   * real one — see `MergedVersionProvenance`. Undefined on anything that came
   * from an upload, which is every version in the registry.
   *
   * Its presence is the reliable test for "this version's `id` is synthetic
   * and must not be persisted".
   */
  mergedFrom?: MergedVersionProvenance;
}

/** Result of parsing one uploaded xlsx. */
export type ParseResult =
  | { ok: true; version: Omit<EstimateVersion, 'id' | 'uploadedAt' | 'uploadedBy'> }
  | { ok: false; errors: string[] };

/** Snapshot of localStorage shape for estimate versions. */
export interface EstimateVersionsStore {
  versions: EstimateVersion[];
}

/**
 * Per-plan binding to an estimate version. Persisted under
 * `planEstimateBindings` in localStorage as a Record<planName, binding>.
 * SF-sourced plans get auto-stamped to the latest version on first view.
 */
export interface PlanEstimateBinding {
  /** The salesPlan name used elsewhere in the app. */
  planName: string;
  /** Which version's estimates this plan is locked to. */
  estimateVersionId: number;
  /** When the binding was first written (mostly for debugging). */
  stampedAt: string;
}

/**
 * Per-SF-plan binding to an estimate version. Persisted server-side in
 * Snowflake `SF_PLAN_ESTIMATE_BINDING` (NOT localStorage) so the binding
 * survives across browsers + users for the same SF plan.
 *
 * Only used for SF source plans (25/26 inventory). 26/27 Snowflake-saved
 * plans persist their binding via `PLAN_VERSION.estimate_version_id`.
 */
export interface SfPlanEstimateBinding {
  /** Salesforce ID of the SF source plan (the SF salesPlanSfId). */
  sfPlanSfId: string;
  /** Which estimate version this SF plan is bound to. */
  estimateVersionId: number;
  /** Snowflake-issued timestamp (ISO 8601) of the last upsert. */
  updatedAt: string | null;
  /** Email of the user who last changed the binding. */
  updatedBy?: string;
}
